import type {
    ReactNode,
} from 'react'

import {
    Button as AriaButton,
    ComboBox as AriaComboBox,
    ComboBoxValue,
    FieldError as AriaFieldError,
    Group,
    Input as AriaInput,
    Label as AriaLabel,
    ListBox,
    ListBoxItem,
    Popover,
    Tag,
    TagGroup,
    TagList,
    Text,
} from 'react-aria-components'

import {
    Check,
    ChevronDown,
    X,
} from 'lucide-react'

import type {
    FormFieldErrorMessage,
} from './form-field'

import { cn } from '@/shared/lib/cn'

export interface MultiSelectOption {
    id: string
    label: string
    description?: string
    isDisabled?: boolean
}

export interface MultiSelectProps {
    label: ReactNode
    options: readonly MultiSelectOption[]

    value?: readonly string[]
    defaultValue?: readonly string[]

    onChange?: (
        value: readonly string[],
    ) => void

    placeholder?: string
    description?: ReactNode
    errorMessage?: FormFieldErrorMessage

    name?: string
    isRequired?: boolean
    isDisabled?: boolean
    isReadOnly?: boolean
    isInvalid?: boolean

    className?: string
}

export function MultiSelect({
    label,
    options,
    value,
    defaultValue,
    onChange,
    placeholder = 'Search and select',
    description,
    errorMessage,
    name,
    isRequired = false,
    isDisabled = false,
    isReadOnly = false,
    isInvalid,
    className,
}: MultiSelectProps) {
    const hasStaticError =
        typeof errorMessage === 'string' &&
        errorMessage.trim().length > 0

    return (
        <AriaComboBox
            selectionMode="multiple"
            items={options}
            value={
                value
                    ? [...value]
                    : undefined
            }
            defaultValue={
                defaultValue
                    ? [...defaultValue]
                    : undefined
            }
            onChange={(nextValue) => {
                const values =
                    Array.isArray(nextValue)
                        ? nextValue.map(String)
                        : []

                onChange?.(values)
            }}
            name={name}
            isRequired={isRequired}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isInvalid={
                isInvalid ?? hasStaticError
            }
            validationBehavior="aria"
            className={cn(
                'flex w-full flex-col gap-1.5',
                className,
            )}
        >
            <AriaLabel className="flex items-center gap-1 text-sm font-medium">
                {label}

                {isRequired ? (
                    <span
                        aria-hidden="true"
                        className="text-(--color-danger)"
                    >
                        *
                    </span>
                ) : null}
            </AriaLabel>

            <Group className="flex min-h-10 items-center rounded-lg border border-(--color-border) bg-(--color-surface) px-3 transition-[border-color,box-shadow] data-focus-within:border-(--color-primary) data-focus-within:ring-2 
            data-focus-within:ring-(--color-primary)/20 data-invalid:border-(--color-danger) data-disabled:opacity-60">
                <AriaInput
                    placeholder={placeholder}
                    className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-(--color-foreground) outline-none placeholder:text-(--color-muted-foreground)"
                />

                <AriaButton
                    slot="trigger"
                    className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md outline-none hover:bg-(--color-surface-muted) focus-visible:ring-2 focus-visible:ring-(--color-primary) data-disabled:cursor-not-allowed"
                    aria-label="Open options"
                >
                    <ChevronDown
                        aria-hidden="true"
                        className="h-4 w-4"
                    />
                </AriaButton>
            </Group>

            <ComboBoxValue<MultiSelectOption>>
                {({ selectedItems, state }) => {
                    const items = selectedItems.filter(
                        (
                            item,
                        ): item is MultiSelectOption =>
                            item !== null,
                    )

                    return (
                        <TagGroup
                            aria-label="Selected options"
                            className="mt-1"
                            onRemove={(keys) => {
                                if (!Array.isArray(state.value)) {
                                    return
                                }

                                state.setValue(
                                    state.value.filter(
                                        (key) => !keys.has(key),
                                    ),
                                )
                            }}
                        >
                            <TagList
                                items={items}
                                renderEmptyState={() => (
                                    <span className="text-xs text-(--color-muted-foreground)">
                                        No selected options
                                    </span>
                                )}
                                className="flex flex-wrap gap-2"
                            >
                                {(item) => (
                                    <Tag
                                        id={item.id}
                                        textValue={item.label}
                                        className={[
                                            'flex items-center gap-1',
                                            'rounded-md border',
                                            'border-(--color-border)',
                                            'bg-(--color-surface-muted)',
                                            'px-2 py-1 text-xs',
                                            'outline-none',
                                            'focus-visible:ring-2',
                                            'focus-visible:ring-(--color-primary)',
                                        ].join(' ')}
                                    >
                                        <span>{item.label}</span>

                                        <AriaButton
                                            slot="remove"
                                            aria-label={`Remove ${item.label}`}
                                            className={[
                                                'flex h-5 w-5',
                                                'items-center justify-center',
                                                'rounded outline-none',
                                                'hover:bg-(--color-border)',
                                                'focus-visible:ring-2',
                                                'focus-visible:ring-(--color-primary)',
                                            ].join(' ')}
                                        >
                                            <X
                                                aria-hidden="true"
                                                className="h-3 w-3"
                                            />
                                        </AriaButton>
                                    </Tag>
                                )}
                            </TagList>
                        </TagGroup>
                    )
                }}
            </ComboBoxValue>
            {description ? (
                <Text
                    slot="description"
                    className="text-xs leading-5 text-(--color-muted-foreground)"
                >
                    {description}
                </Text>
            ) : null}

            <AriaFieldError className="text-xs leading-5 text-(--color-danger)">
                {errorMessage}
            </AriaFieldError>

            <Popover
                placement="bottom start"
                offset={6}
                className="z-50 w-(--trigger-width) overflow-hidden rounded-lg border border-(--color-border) bg-(--color-surface) shadow-[var(--shadow-popover)] outline-none"
            >
                <ListBox
                    items={options}
                    className="max-h-72 overflow-y-auto p-1 outline-none"
                >
                    {(option) => (
                        <ListBoxItem
                            id={option.id}
                            textValue={option.label}
                            isDisabled={
                                option.isDisabled
                            }
                            className={({ isSelected }) =>
                                cn(
                                    [
                                        'flex cursor-pointer',
                                        'items-center gap-3',
                                        'rounded-md px-3 py-2',
                                        'text-sm outline-none',
                                        'data-focused:bg-(--color-primary-muted)',
                                        'data-disabled:cursor-not-allowed',
                                        'data-disabled:opacity-50',
                                    ],
                                    isSelected &&
                                    'text-(--color-primary)',
                                )
                            }
                        >
                            {({ isSelected }) => (
                                <>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium">
                                            {option.label}
                                        </div>

                                        {option.description ? (
                                            <div className="truncate text-xs text-(--color-muted-foreground)">
                                                {
                                                    option.description
                                                }
                                            </div>
                                        ) : null}
                                    </div>

                                    {isSelected ? (
                                        <Check
                                            aria-hidden="true"
                                            className="h-4 w-4 shrink-0"
                                        />
                                    ) : null}
                                </>
                            )}
                        </ListBoxItem>
                    )}
                </ListBox>
            </Popover>
        </AriaComboBox>
    )
}
